import agentWebSocket from "@app/config/agentWebSocket";
import { useClearAgentSession } from "@app/queries/agent";
import type { AgentChatMode } from "@app/state/types";
import { useAuth0 } from "@auth0/auth0-react";
import { confirm } from "@lib/common/components/modals/modals";
import AgentChat from "@lib/experiment/agent/AgentChat/AgentChat";
import { useAgentChatContext } from "@lib/experiment/agent/AgentChatContext";
import ChatSessionUserFeedback from "@lib/experiment/agent/ChatSessionUserFeedback/ChatSessionUserFeedback";
import { Text } from "@mantine/core";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export type ExperimentAgentChatVariant = "draft" | "experiment";

type ExperimentAgentChatProps = {
  chatMode: AgentChatMode;
  setChatMode: (mode: AgentChatMode) => void;
  toggle: (value: boolean) => void;
  variant?: ExperimentAgentChatVariant;
};

export default function ExperimentAgentChat({
  chatMode,
  toggle,
  setChatMode,
  variant = "experiment"
}: ExperimentAgentChatProps) {
  const {
    experimentId,
    teamId,
    messages,
    isStreaming,
    connectionState,
    send,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    allowReseed,
    hasRestoredHistory,
    agentSessionId,
    committedMessageCount
  } = useAgentChatContext();
  const clearMutation = useClearAgentSession();
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuerySentRef = useRef(false);
  const isDraft = variant === "draft";

  // Draft entry only: forward `?query=...` as the first user message, then strip it from the
  // URL/history so reload/back-nav doesn't resend.
  // Wait for the socket *and* the history fetch to resolve before deciding — otherwise a failed
  // history fetch would burn the one-shot flag and lose the query param across a retry.
  useEffect(() => {
    if (!isDraft) {
      return;
    }
    if (connectionState !== "connected" || isHistoryLoading || historyError || initialQuerySentRef.current) {
      return;
    }
    initialQuerySentRef.current = true;
    if (hasRestoredHistory) {
      return;
    }

    const initialQuery = searchParams.get("query");
    if (!initialQuery) {
      return;
    }

    send(initialQuery);

    const next = new URLSearchParams(searchParams);
    next.delete("query");
    void navigate(
      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
      { replace: true }
    );
  }, [
    isDraft,
    connectionState,
    isHistoryLoading,
    historyError,
    hasRestoredHistory,
    searchParams,
    send,
    navigate,
    location.pathname
  ]);

  const handleClear = useCallback(async () => {
    // Optimistic: clear the UI immediately. The DELETE drops the DB row + cascade
    // messages; closing the WS forces the consumer's `disconnect` handler to evict
    // the cached Strands Agent so the next prompt rebuilds fresh state. We then
    // reconnect so the chat is usable again without a manual reload.
    clearLocal();
    agentWebSocket.close();
    try {
      await clearMutation.mutateAsync({ params: { path: { experiment_id: experimentId } } });
    } catch {
      // Notification is raised by the mutation's onError. Re-open the seed gate so the
      // refetched history rehydrates the cleared UI back to the original conversation.
      allowReseed();
      void refetchHistory();
    }
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
  }, [clearLocal, clearMutation, experimentId, teamId, allowReseed, refetchHistory, getAccessTokenSilently]);

  const onClear = useCallback(() => {
    const subject = isDraft ? "draft" : "experiment";
    confirm({
      title: "Clear conversation",
      danger: true,
      labels: { confirm: "Clear" },
      children: <Text size="sm">This permanently removes the chat history for this {subject}.</Text>,
      onConfirm: () => void handleClear()
    });
  }, [handleClear, isDraft]);

  const clearDisabled = isStreaming || isHistoryLoading || clearMutation.isPending || messages.length === 0;

  const userFeedback = agentSessionId ? (
    <ChatSessionUserFeedback
      agentSessionId={agentSessionId}
      disabled={isStreaming}
      messageCount={committedMessageCount}
    />
  ) : null;

  return (
    <AgentChat
      messages={messages}
      isStreaming={isStreaming}
      connectionState={connectionState}
      send={send}
      stop={stop}
      isHistoryLoading={isHistoryLoading}
      historyError={historyError}
      onRetryHistory={() => void refetchHistory()}
      onClear={onClear}
      disabledClear={clearDisabled}
      userFeedback={userFeedback}
      variant={variant}
      toggle={toggle}
      chatMode={chatMode}
      setChatMode={setChatMode}
    />
  );
}
